import { useCallback, useEffect, useState, type RefObject } from 'react';

export type AnchorPlacement = 'top' | 'bottom' | 'left' | 'right';
/** Horizontal alignment for top/bottom placements — ignored for left/right. */
export type AnchorAlign = 'center' | 'start' | 'end';

export interface AnchoredPosition {
  top: number;
  left: number;
  transform: string;
}

const HIDDEN_POSITION: AnchoredPosition = { top: -9999, left: -9999, transform: 'none' };

function horizontalAnchor(rect: DOMRect, align: AnchorAlign): { left: number; xTransform: string } {
  switch (align) {
    case 'start':
      return { left: rect.left, xTransform: '0' };
    case 'end':
      return { left: rect.right, xTransform: '-100%' };
    case 'center':
    default:
      return { left: rect.left + rect.width / 2, xTransform: '-50%' };
  }
}

function computePosition(rect: DOMRect, placement: AnchorPlacement, gap: number, align: AnchorAlign): AnchoredPosition {
  switch (placement) {
    case 'top': {
      const { left, xTransform } = horizontalAnchor(rect, align);
      return { top: rect.top - gap, left, transform: `translate(${xTransform}, -100%)` };
    }
    case 'bottom': {
      const { left, xTransform } = horizontalAnchor(rect, align);
      return { top: rect.bottom + gap, left, transform: `translate(${xTransform}, 0)` };
    }
    case 'left':
      return { top: rect.top + rect.height / 2, left: rect.left - gap, transform: 'translate(-100%, -50%)' };
    case 'right':
      return { top: rect.top + rect.height / 2, left: rect.right + gap, transform: 'translate(0, -50%)' };
  }
}

/** Space between a flipped/clamped overlay and the viewport edge. */
const VIEWPORT_MARGIN = 8;

/**
 * Flip the placement when the overlay would open off-screen, then clamp so
 * it can never sit outside the viewport at all.
 *
 * Without this an overlay anchored near the bottom of the viewport (the last
 * row of a long table is the common case) renders below the fold — and
 * because the portalled node is `position: fixed` and re-tracks its anchor on
 * scroll, scrolling never brings it back. The overlay is simply unreachable.
 *
 * `overlaySize` comes from measuring the rendered node; until it is known we
 * return the unflipped position, which is what the first paint uses.
 */
function fitToViewport(
  pos: AnchoredPosition,
  rect: DOMRect,
  placement: AnchorPlacement,
  gap: number,
  overlaySize: { width: number; height: number } | null,
): AnchoredPosition {
  if (!overlaySize || typeof window === 'undefined') return pos;

  const { width: vw, height: vh } = { width: window.innerWidth, height: window.innerHeight };
  const { width: ow, height: oh } = overlaySize;
  let { top, left, transform } = pos;

  // ── Vertical flip (top/bottom placements) ──────────────────────────────
  if (placement === 'bottom' && rect.bottom + gap + oh > vh - VIEWPORT_MARGIN) {
    const flippedTop = rect.top - gap;
    // Only flip if the other side actually has room — otherwise clamping
    // below is the better outcome than swapping one overflow for another.
    if (flippedTop - oh >= VIEWPORT_MARGIN) {
      top = flippedTop;
      transform = transform.replace(/,\s*0\)$/, ', -100%)');
    }
  } else if (placement === 'top' && rect.top - gap - oh < VIEWPORT_MARGIN) {
    const flippedTop = rect.bottom + gap;
    if (flippedTop + oh <= vh - VIEWPORT_MARGIN) {
      top = flippedTop;
      transform = transform.replace(/,\s*-100%\)$/, ', 0)');
    }
  }

  // ── Clamp ──────────────────────────────────────────────────────────────
  // Resolve the transform's effect on the box so clamping works on real
  // edges rather than on the untransformed anchor point.
  const xShift = transform.includes('-100%,') ? -ow : transform.includes('-50%,') ? -ow / 2 : 0;
  const yShift = transform.includes(', -100%)') ? -oh : transform.includes(', -50%)') ? -oh / 2 : 0;

  const realLeft = left + xShift;
  const realTop = top + yShift;

  if (realLeft < VIEWPORT_MARGIN) left += VIEWPORT_MARGIN - realLeft;
  else if (realLeft + ow > vw - VIEWPORT_MARGIN) left -= realLeft + ow - (vw - VIEWPORT_MARGIN);

  if (realTop < VIEWPORT_MARGIN) top += VIEWPORT_MARGIN - realTop;
  else if (realTop + oh > vh - VIEWPORT_MARGIN) top -= realTop + oh - (vh - VIEWPORT_MARGIN);

  return { top, left, transform };
}

/**
 * Tracks viewport-relative fixed-position coordinates for a portalled
 * overlay anchored to an in-page element. Because the portalled node is
 * `position: fixed`, `getBoundingClientRect()` coords map directly to
 * `top`/`left` — no offset-parent math needed.
 *
 * Recomputes on resize, on any ancestor scroll (capture phase — required so
 * scrolling a clipped ancestor like a table's overflow-x wrapper re-tracks
 * the anchor), and on anchor content reflow via ResizeObserver.
 *
 * Pass `overlayRef` to enable viewport flip/clamp: the overlay's own size is
 * measured so it can never render off-screen. Omitting it keeps the raw
 * anchor-relative behaviour.
 */
export function useAnchoredPosition(
  anchorRef: RefObject<HTMLElement | null>,
  placement: AnchorPlacement,
  isOpen: boolean,
  gap = 8,
  align: AnchorAlign = 'center',
  overlayRef?: RefObject<HTMLElement | null>,
): AnchoredPosition {
  const [position, setPosition] = useState<AnchoredPosition>(HIDDEN_POSITION);

  const recompute = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const raw = computePosition(rect, placement, gap, align);
    const overlayEl = overlayRef?.current;
    const overlaySize = overlayEl
      ? { width: overlayEl.offsetWidth, height: overlayEl.offsetHeight }
      : null;
    setPosition(fitToViewport(raw, rect, placement, gap, overlaySize));
  }, [anchorRef, placement, gap, align, overlayRef]);

  useEffect(() => {
    if (!isOpen) return;

    recompute();

    let frame = 0;
    const scheduleRecompute = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(recompute);
    };

    window.addEventListener('resize', scheduleRecompute);
    window.addEventListener('scroll', scheduleRecompute, { capture: true });

    const resizeObserver = new ResizeObserver(scheduleRecompute);
    if (anchorRef.current) resizeObserver.observe(anchorRef.current);
    // Observe the overlay too: its height is unknown on the first paint, so
    // the flip/clamp decision has to be re-made once it has actually
    // rendered — otherwise a tall menu still opens off-screen on first open.
    if (overlayRef?.current) resizeObserver.observe(overlayRef.current);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', scheduleRecompute);
      window.removeEventListener('scroll', scheduleRecompute, { capture: true });
      resizeObserver.disconnect();
    };
  }, [isOpen, recompute, anchorRef, overlayRef]);

  return isOpen ? position : HIDDEN_POSITION;
}
